import { ArrowDown } from 'lucide-react';
import { useRef, useEffect, useState } from "react";
import Header from '../components/common/Header';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useLanguage } from '../contexts/LanguageContext';

const PrivacyPolicy = ({
  P1 = "PRIVACY POLICY",
  P2 = "Personal Information Governance Policy",
  P3 = "Last Updated: 06/02/2025",
  P5 = "At EagleVizion Ltd., protecting personal information is a priority. We are committed to managing this information responsibly, in compliance with Quebec's Law 25 and best privacy practices.",
  P6 = "2. Collection of Personal Information",
  P7 = "We collect only the information necessary for our activities and services. This information may include:",
  P8 = "First and last name",
  P9 = "Email address",
  P10 = "Phone number",
  P11 = "Postal address",
  P12 = "Billing information",
  P13 = "Any other relevant information related to our services",
  P14 = "3. Use of Personal Information",
  P15 = "The collected information is used for the following purposes:",
  P16 = "Provide and improve our services",
  P17 = "Communicate with our clients and partners",
  P18 = "Comply with our legal and regulatory obligations",
  P19 = "Ensure security and prevent fraud",
  P20 = "4. Retention and Destruction of Information",
  P21 = "We retain personal information only as long as necessary to fulfill the purposes for which it was collected. Once these purposes are met, we securely dispose of it.",
  P22 = "5. Protection of Personal Information",
  P23 = "We implement security measures to protect your information from unauthorized access, loss, alteration, or disclosure. These measures include:",
  P24 = "Restricted access for authorized employees",
  P25 = "Encryption of sensitive data",
  P26 = "Regular updates to our security systems",
  P27 = "6. Individual Rights",
  P28 = "In accordance with Law 25, you have the following rights:",
  P29 = "Access and Correction:",
  P30 = "You may request access to your personal information and have it corrected if there are errors.",
  P31 = "Withdrawal of Consent:",
  P32 = "You may withdraw your consent to the use of your information, subject to legal requirements.",
  P33 = "Complaint:",
  P34 = "If you believe your information is not being handled in accordance with this policy, you may contact us.",
  P35 = "7. Personal Information Protection Officer",
  P36 = "For any questions or requests regarding your personal information, you may contact:",
  P37 = "3425 Boulevard Industriel,",
  P38 = "8. Policy Modifications",
  P39 = "We may update this policy at any time to reflect legislative changes or our internal practices. Any modifications will be posted on our website.",
  P40 = "We respect your privacy.",
  P41 = "We use cookies to enhance your browsing experience, deliver personalized ads or content, and analyze traffic. By clicking 'Accept All,' you consent to our use of cookies.",
  P42 = "Download PRIVACY POLICY",
  P43 = "This policy explains how we collect, use, retain, and protect your personal information.",
}) => {
  const policyRef = useRef(null);
  const { currentLanguage, translateContent } = useLanguage();
  const [translations, setTranslations] = useState({
    P1, P2, P3, P5, P6, P7, P8, P9, P10,
    P11, P12, P13, P14, P15, P16, P17, P18, P19, P20,
    P21, P22, P23, P24, P25, P26, P27, P28, P29, P30,
    P31, P32, P33, P34, P35, P36, P37, P38, P39, P40,
    P41, P42, P43,
  });

  useEffect(() => {
    const translateAll = async () => {
      const keys = [
        'P1', 'P2', 'P3', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10',
        'P11', 'P12', 'P13', 'P14', 'P15', 'P16', 'P17', 'P18', 'P19', 'P20',
        'P21', 'P22', 'P23', 'P24', 'P25', 'P26', 'P27', 'P28', 'P29', 'P30',
        'P31', 'P32', 'P33', 'P34', 'P35', 'P36', 'P37', 'P38', 'P39', 'P40',
        'P41', 'P42', 'P43',
      ];
      const originalTexts = {
        P1, P2, P3, P5, P6, P7, P8, P9, P10,
        P11, P12, P13, P14, P15, P16, P17, P18, P19, P20,
        P21, P22, P23, P24, P25, P26, P27, P28, P29, P30,
        P31, P32, P33, P34, P35, P36, P37, P38, P39, P40,
        P41, P42, P43,
      };

      const newTranslations = {};

      const translationPromises = keys.map(async (key) => {
        const translated = await translateContent(originalTexts[key]);
        return { key, translated };
      });

      const results = await Promise.all(translationPromises);

      results.forEach(({ key, translated }) => {
        newTranslations[key] = translated;
      });

      setTranslations(newTranslations);
    };

    translateAll();
  }, [currentLanguage,
    P1, P2, P3, P5, P6, P7, P8, P9, P10,
    P11, P12, P13, P14, P15, P16, P17, P18, P19, P20,
    P21, P22, P23, P24, P25, P26, P27, P28, P29, P30,
    P31, P32, P33, P34, P35, P36, P37, P38, P39, P40,
    P41, P42, P43, translateContent]);


    const handleDownload = async () => {
      const content = policyRef.current;
      if (!content) return;
  
      const pdf = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: "a4",
          compress: false
      });
  
      // PDF dimensions
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      let yPosition = margin;
  
      // Logo dimensions and position
      const logoWidth = 40; // mm
      const logoHeight = 15; // mm
      const logoMargin = 15; // mm from left and top
      const logoUrl = 'assets/img/logo/EVLogo.png';
  
      // Configure PDF metadata
      pdf.setProperties({
          title: "Politique de Confidentialité",
          subject: "Privacy Policy",
          creator: "EagleVizion",
          author: "EagleVizion"
      });
  
      // Get all elements except the download button
      const elements = Array.from(content.children).filter(element => 
          !element.classList.contains('sbroem-privacy-download-btn')
      );
  
      // Function to load and add logo to current page
      const addLogoToPage = async () => {
          try {
              // Create a new image element
              const logoImg = new Image();
              logoImg.crossOrigin = "anonymous";  // Handle CORS if needed
              
              // Create a promise to handle image loading
              const logoLoaded = new Promise((resolve, reject) => {
                  logoImg.onload = () => resolve(logoImg);
                  logoImg.onerror = (error) => reject(error);
                  logoImg.src = logoUrl;
              });
  
              // Wait for image to load
              const loadedLogo = await logoLoaded;
              
              // Create canvas and draw logo
              const logoCanvas = document.createElement('canvas');
              logoCanvas.width = loadedLogo.width;
              logoCanvas.height = loadedLogo.height;
              const ctx = logoCanvas.getContext('2d');
              ctx.drawImage(loadedLogo, 0, 0);
              
              // Add to PDF
              const logoData = logoCanvas.toDataURL('image/png');
              pdf.addImage(logoData, 'PNG', logoMargin, logoMargin, logoWidth, logoHeight);
          } catch (error) {
              console.error('Error adding logo:', error);
          }
      };
  
      // Add logo to first page
      await addLogoToPage();
      
      // Adjust starting position to account for logo
      yPosition = logoMargin + logoHeight + 10;
  
      // html2canvas configuration for better quality
      const canvasConfig = {
          scale: 3,
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          removeContainer: true,
          windowWidth: 1200,
          onclone: (clonedDoc) => {
              const style = clonedDoc.createElement('style');
              style.innerHTML = `
                  * {
                      -webkit-font-smoothing: antialiased;
                      text-rendering: optimizeLegibility;
                  }
                  .sbroem-privacy-text {
                      font-size: 14px;
                      line-height: 1.6;
                  }
                  .sbroem-privacy-heading {
                      margin-top: 20px;
                      margin-bottom: 10px;
                  }
              `;
              clonedDoc.head.appendChild(style);
          }
      };
  
      for (let i = 0; i < elements.length; i++) {
          const element = elements[i];
  
          const canvas = await html2canvas(element, canvasConfig);
          const imgData = canvas.toDataURL("image/jpeg", 1.0);
  
          const imgWidth = pageWidth - (2 * margin);
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
          if (yPosition + imgHeight > pageHeight - margin) {
              pdf.addPage();
              // Add logo to new page
              await addLogoToPage();
              yPosition = logoMargin + logoHeight + 10;
          }
  
          pdf.addImage(imgData, "JPEG", margin, yPosition, imgWidth, imgHeight, undefined, 'FAST');
          yPosition += imgHeight + (element.tagName === 'H2' ? 10 : 5);
      }
  
      // Add page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(100);
          pdf.text(
              `Page ${i} of ${pageCount}`,
              pageWidth / 2,
              pageHeight - 10,
              { align: 'center' }
          );
      }
  
      pdf.save("Politique_de_Confidentialite.pdf");
  };
  return (
        <>
    <div>
    {/* Navigation bar */}
    {/* <NavBar
      devis="obtenir un devis"
      phone="Téléphone"
      addresse="Adresse"
      a1="3425 Bd Industriel, Sherbrooke,"
      a2="QC J1L 2W1"
      acc="ACCUEIL"
      about="A PROPOS"
      contact="CONTACT"
      produit="PRODUIT"
      spotlight="SOUS LES FEUX DE LA RAMPE"
      EVENEMENTS="EVENEMENTS" politique="POLITIQUE DE CONFIDENTIALITÉ"
    /> */}
  <Header />
  <br></br>
    <br></br>
  <br></br>

    {/* Breadcrumb */}
    <div className="breadcrumb-area breadcrumb-bg">
      <div className="container">
        <div className="row">
          <div className="col">
            <div className="page-banner text-center">
              <h1>{translations.P1}</h1>
              <ul className="page-breadcrumb">
               
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="container" style={{ position: 'relative' }}>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <div ref={policyRef} className="sbroem-privacy-wrapper">
            <div className="sbroem-privacy-header">
              <h1 className="sbroem-privacy-title">{translations.P2}</h1>
              <div className="sbroem-privacy-date-container">
                <p className="sbroem-privacy-date">{translations.P3}</p>
              </div>
               <div className="sbroem-privacy-date-container">
                <p className="sbroem-privacy-date">
                    {currentLanguage === 'fr'
                      ? "Cette politique de confidentialité, rédigée à l'origine en français, a été traduite en anglais pour votre commodité. En cas de divergence, la version française prévaut."
                      : "This Privacy Policy, originally drafted in French, has been translated into English for your convenience. In the event of any discrepancies, the French version shall prevail as the authoritative document."}
                  </p>
              </div>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">1. Introduction
              </h2>
              <p className="sbroem-privacy-text">
              {translations.P5}
              </p>
              <p className="sbroem-privacy-text">
              {translations.P43}
              </p>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">{translations.P6}</h2>
              <p className="sbroem-privacy-text">
              {translations.P7}
              </p>
              <ul className="sbroem-privacy-list">
                <li className="sbroem-privacy-list-item">{translations.P8}</li>
                <li className="sbroem-privacy-list-item">{translations.P9}</li>
                <li className="sbroem-privacy-list-item">{translations.P10}</li>
                <li className="sbroem-privacy-list-item">{translations.P11}</li>
                <li className="sbroem-privacy-list-item">{translations.P12}</li>
                <li className="sbroem-privacy-list-item">
                {translations.P13}
                </li>
              </ul>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">{translations.P14}</h2>
              <p className="sbroem-privacy-text">
              {translations.P15}
              </p>
              <ul className="sbroem-privacy-list">
                <li className="sbroem-privacy-list-item">{translations.P16}</li>
                <li className="sbroem-privacy-list-item">{translations.P17}</li>
                <li className="sbroem-privacy-list-item">{translations.P18}</li>
                <li className="sbroem-privacy-list-item">{translations.P19}</li>
              </ul>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">{translations.P20}</h2>
              <p className="sbroem-privacy-text">
              {translations.P21}
              </p>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">{translations.P22}</h2>
              <p className="sbroem-privacy-text">
              {translations.P23}
              </p>
              <ul className="sbroem-privacy-list">
                <li className="sbroem-privacy-list-item">{translations.P24}</li>
                <li className="sbroem-privacy-list-item">{translations.P25}</li>
                <li className="sbroem-privacy-list-item">
                {translations.P26}
                </li>
              </ul>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">{translations.P27}</h2>
              <p className="sbroem-privacy-text">
              {translations.P28}
              </p>
              <ul className="sbroem-privacy-list">
                <li className="sbroem-privacy-list-item">
                  <span className="sbroem-privacy-bold">{translations.P29}</span> {translations.P30}
                </li>
                <li className="sbroem-privacy-list-item">
                  <span className="sbroem-privacy-bold">{translations.P31}</span> {translations.P32}
                </li>
                <li className="sbroem-privacy-list-item">
                  <span className="sbroem-privacy-bold">{translations.P33}</span> {translations.P34}
                </li>
              </ul>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">
              {translations.P35}
              </h2>
              <p className="sbroem-privacy-text">
              {translations.P36}
              </p>
              <div className="sbroem-privacy-contact">
                <p className="sbroem-privacy-contact-item">Jeremy Ouellet</p>
                <p className="sbroem-privacy-contact-item">jouellet@sherbrooke-oem.com</p>
                <p className="sbroem-privacy-contact-item">{translations.P37}</p>
                <p className="sbroem-privacy-contact-item">Sherbrooke, Qc</p>
                <p className="sbroem-privacy-contact-item">J1L 2W1</p>
                <p className="sbroem-privacy-contact-item">819-342-5737</p>
              </div>
            </div>

            <div className="sbroem-privacy-section">
              <h2 className="sbroem-privacy-heading">{translations.P38}</h2>
              <p className="sbroem-privacy-text">
              {translations.P39}
              </p>
            </div>

            <div className="sbroem-privacy-cookie-notice">
              <p className="sbroem-privacy-text">{translations.P40}</p>
              <p className="sbroem-privacy-text">
              {translations.P41}
              </p>
            </div>
            <button className="sbroem-privacy-download-btn" onClick={handleDownload}>
                  <ArrowDown  className="sbroem-privacy-download-icon" />
                   <span >{translations.P42}</span>
                </button>
                
          </div>
        </div>
        
      </div>
    </div>
    {/* <BrandLogoSlider /> */}

{/* Footer */}
{/* <Footer
  lien="LIENS UTILES"
  acuueilfoo="ACCUEIL"
  produit="PRODUIT"
  fabout="A PROPOS"
  fcontact="CONTACT"
  CONTACTEZ="CONTACTEZ-NOUS"
  fa1="3425 Bd Industriel, Sherbrooke,"
  fa2="QC J1L 2W1"
  fsh1="Sherbrooke OEM"
  fsh2="développé par OEM ENGINEERING"
  spotlight="SOUS LES FEUX DE LA RAMPE" EVENEMENTS="EVENEMENTS" politique="POLITIQUE DE CONFIDENTIALITÉ"
/>
<MobileMenu
  maccueil="ACCUEIL"
  mpropos="A PROPOS"
  produit="PRODUIT"
  mcontact="CONTACT"
  spotlight="SOUS LES FEUX DE LA RAMPE" EVENEMENTS="EVENEMENTS" politique="POLITIQUE DE CONFIDENTIALITÉ"
/> */}
</div>
</>
  );
};

export default PrivacyPolicy;